/**
 * Briques des pages de saisie à étapes : une carte numérotée par étape,
 * révélée quand la précédente est remplie, puis le pied d'envoi collant qui
 * dit ce qui manque avant d'activer le bouton. Gabarit : CreatePage
 * (components/templates).
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './ui.js';
import { Banner } from './ui/Feedback.js';

/** Une étape : numéro, titre, explication courte, puis ses champs. */
export function FormStep(props: {
  n: number;
  title: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="form-step ui-step" aria-labelledby={`etape-${props.n}`}>
      <div className="ui-step__head">
        <h2 className="ui-step__title" id={`etape-${props.n}`}>
          <span className="ui-step__number" aria-hidden="true">{props.n}</span> {props.title}
        </h2>
        {props.hint ? <p className="ui-step__hint">{props.hint}</p> : null}
      </div>
      {props.children}
    </section>
  );
}

/**
 * Verrou d'envoi, logique pure : un appui, un envoi. Le bouton se verrouille
 * au premier appui jusqu'à la fin de l'envoi qu'il a lancé ; un appui qui ne
 * lance rien (refus local) le libère après un court délai.
 */
export function submitLock(
  later: (callback: () => void, ms: number) => void = (callback, ms) => void window.setTimeout(callback, ms),
): { tryStart: (pending: boolean) => boolean; pendingChanged: (pending: boolean) => void } {
  let locked = false;
  let started = false;
  return {
    pendingChanged(pending) {
      if (pending) started = true;
      else if (started) {
        started = false;
        locked = false;
      }
    },
    tryStart(pending) {
      if (locked || pending) return false;
      locked = true;
      started = false;
      later(() => {
        if (!started) locked = false;
      }, 600);
      return true;
    },
  };
}

/**
 * L'état « en cours » d'une requête n'arrive à l'écran qu'au tour suivant :
 * sans verrou, un double appui rapide sur un téléphone lent envoyait deux fois
 * la même saisie.
 */
export function useSubmitOnce(pending: boolean, submit: () => void): () => void {
  const lock = useRef<ReturnType<typeof submitLock>>(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
  lock.current ??= submitLock();
  useEffect(() => lock.current?.pendingChanged(pending), [pending]);
  return () => {
    if (lock.current?.tryStart(pending)) submit();
  };
}

/** Pied d'envoi : ce qui manque (ou un résumé), l'erreur serveur, puis le bouton. */
export function FormFooter(props: {
  label: string;
  problem: string | null;
  ready: ReactNode;
  pending: boolean;
  error: unknown;
  onSave: () => void;
  action: ReactNode;
  pendingLabel?: string;
}): ReactNode {
  const save = useSubmitOnce(props.pending, props.onSave);
  return (
    <div className="ui-form-footer" role="region" aria-label={props.label} data-ui="action-bar">
      {props.problem ? (
        <p className="ui-form-footer__problem" role="status">{props.problem}</p>
      ) : (
        <p className="ui-form-footer__ready">{props.ready}</p>
      )}
      {props.error ? <Banner tone="danger">{(props.error as Error).message}</Banner> : null}
      <Button large disabled={Boolean(props.problem) || props.pending} onClick={save}>
        {props.pending ? (props.pendingLabel ?? 'Enregistrement…') : props.action}
      </Button>
    </div>
  );
}
